import { z } from 'zod';
import type { Platform } from './common';
import { compareVersions, semverSchema } from './version';

/**
 * Minimum app version per platform (review S7; additive, apiVersion stays 1). Served next to
 * `minAppVersion` on the manifest and the directory, so the iPhone and Android apps can be
 * gated separately (Apple's review lags Android). Absent = use `minAppVersion`.
 */
export const minAppVersionByPlatformSchema = z
  .object({
    ios: semverSchema.optional(),
    android: semverSchema.optional(),
    web: semverSchema.optional(),
  })
  .strict();
export type MinAppVersionByPlatform = z.infer<typeof minAppVersionByPlatformSchema>;

/** The higher of the overall minimum and this platform's minimum. */
export function effectiveMinAppVersion(
  doc: { minAppVersion: string; minAppVersionByPlatform?: MinAppVersionByPlatform | undefined },
  platform: Platform,
): string {
  const own = doc.minAppVersionByPlatform?.[platform];
  if (!own) return doc.minAppVersion;
  return compareVersions(own, doc.minAppVersion) > 0 ? own : doc.minAppVersion;
}

/** Env names per platform (tenant-api and central-api); unset or empty = no platform minimum. */
export const MIN_APP_VERSION_ENV = {
  ios: 'MIN_APP_VERSION_IOS',
  android: 'MIN_APP_VERSION_ANDROID',
  web: 'MIN_APP_VERSION_WEB',
} as const;

/** Builds `minAppVersionByPlatform` from env; undefined when none is set. Throws on a bad value. */
export type MinAppVersionEnv = {
  readonly [K in (typeof MIN_APP_VERSION_ENV)[keyof typeof MIN_APP_VERSION_ENV]]?:
    string | undefined;
};

export function minAppVersionByPlatformFromEnv(
  env: MinAppVersionEnv,
): MinAppVersionByPlatform | undefined {
  const entries = (Object.keys(MIN_APP_VERSION_ENV) as (keyof typeof MIN_APP_VERSION_ENV)[])
    .map((platform) => [platform, env[MIN_APP_VERSION_ENV[platform]]] as const)
    .filter((entry): entry is readonly [(typeof entry)[0], string] => Boolean(entry[1]));
  for (const [platform, value] of entries) {
    if (!semverSchema.safeParse(value).success) {
      throw new Error(
        `${MIN_APP_VERSION_ENV[platform]} must be a version like 1.2.3 (got "${value}")`,
      );
    }
  }
  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
}
